<script setup lang="ts">
// Panel 3: where visits leave no record. A Site label is a link into the
// existing Site detail card — the workbench's selection watch opens it — so the
// ranked list is a way to read the places the visits happen, not a dead tally.
//
// Each row is one horizontal stack of the Site's visits, split into the same
// five outcomes the outcome bar and the weekly stack draw, in the same order
// and colours: the row's length is the Site's visits, and a segment's width is
// that outcome's share of them. A six-column table could not fit the rail; a
// stack says the same thing in the width one number used to take. The key for
// those colours lives in this card rather than beside it, because enlarging a
// card moves only the card's own DOM into the overlay: a key in the pane would
// stay behind and the enlarged list would name none of its segments.
import { useStoreMapReview } from "@/store/map-review";
import type { MapReviewWasteSite, MapReviewWasteView } from "@/type/api";

import ReportPanel from "../report/ReportPanel.vue";
import WasteOutcomeKey from "./WasteOutcomeKey.vue";
import { WASTE_OUTCOMES, formatCount, wasteShare } from "./wasteTheme";

defineProps<{
	view: MapReviewWasteView;
}>();

const store = useStoreMapReview();

interface SiteSegment {
	key: string;
	color: string;
	width: string;
	title: string;
}

/** One Site's five outcome counts as bar segments. The counts partition the
 * Site's visits, so the widths sum to the whole bar. */
function segmentsFor(site: MapReviewWasteSite): SiteSegment[] {
	return WASTE_OUTCOMES.filter((outcome) => site[outcome.key] > 0).map(
		(outcome) => {
			const count = site[outcome.key];
			const share = wasteShare(count, site.visits);
			return {
				key: outcome.key,
				color: outcome.color,
				width: `${share}%`,
				title: `${outcome.label}: ${formatCount(count)} visits (${share.toFixed(1)}%)`,
			};
		},
	);
}
</script>

<template>
	<section class="waste-sites">
		<ReportPanel title="Where visits leave no record">
			<p class="panel-help">
				The Sites taking the most visits that recorded neither a check nor a
				treatment, this season. The bar is each Site's visits split by what they
				recorded, so its length is the visits and each block's width is that
				outcome's share. Site labels are the district's own; unlabelled parcels
				carry their number.
			</p>

			<p v-if="view.sites.length === 0" class="panel-empty">
				No Site carries a visit in the current selection.
			</p>
			<template v-else>
				<ul class="site-list">
					<li v-for="site in view.sites" :key="site.id" class="site-row">
						<button
							type="button"
							class="link"
							:title="`Open ${site.label}`"
							@click="store.select('site', site.id)"
						>
							{{ site.label }}
						</button>
						<div
							class="visit-bar"
							role="img"
							:aria-label="`${site.label}: ${formatCount(site.visits)} visits, split by outcome`"
						>
							<span
								v-for="segment in segmentsFor(site)"
								:key="segment.key"
								class="visit-segment"
								:style="{ width: segment.width, background: segment.color }"
								:title="segment.title"
							/>
						</div>
						<span
							class="site-visits"
							:title="`${formatCount(site.visits)} visits`"
							>{{ formatCount(site.visits) }}</span
						>
					</li>
				</ul>
				<WasteOutcomeKey />
			</template>

			<p class="note">
				A visit is one Site and one local day, so these counts are visit-days,
				not separate trips.
			</p>

			<template #help>
				<p>
					The list is ranked by the visits that recorded neither a check nor a
					treatment, and each row splits that Site's whole visit count into the
					same five outcomes as the split bar above. A Site's five counts sum to
					its visits.
				</p>
				<p>
					Site labels are the district's own. Selecting one opens its detail
					card; the list itself is not a filter, because a Site id is not a
					clause any ledger column accepts.
				</p>
			</template>
		</ReportPanel>
	</section>
</template>

<style scoped>
.waste-sites {
	grid-column: 1 / -1;
	min-width: 0;
}

.panel-help {
	margin: 0 0 8px;
	font-size: 0.68rem;
	line-height: 1.35;
	color: #6c757d;
}

.panel-empty {
	margin: 0;
	font-size: 0.72rem;
	color: #6c757d;
}

.site-list {
	margin: 0;
	padding: 0;
	list-style: none;
}

/* Three fixed roles: a name column that truncates, a bar that takes whatever is
   left, and the visit count at the end. Nothing in the row can widen the list
   past the panel, because only the middle track is fluid. */
.site-row {
	display: grid;
	grid-template-columns: 118px minmax(0, 1fr) auto;
	align-items: center;
	gap: 8px;
	padding: 4px 0;
	min-width: 0;
}

.site-row + .site-row {
	border-top: 1px solid #f1f3f5;
}

/* A Site label is a link, not a filter: it opens the detail card while the
   report keeps its current selection. It gets a fixed column and ellipsis
   because the row's length belongs to the bar; the full name is its tooltip. */
.link {
	display: block;
	min-width: 0;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
	padding: 0;
	border: 0;
	background: none;
	color: #0d6efd;
	font: inherit;
	font-size: 0.7rem;
	text-align: left;
	cursor: pointer;
}

.link:hover {
	text-decoration: underline;
}

.link:focus-visible {
	outline: 2px solid #0d6efd;
	outline-offset: 2px;
}

.visit-bar {
	display: flex;
	min-width: 0;
	height: 10px;
	overflow: hidden;
	border-radius: 2px;
	background: #eef0f2;
}

.visit-segment {
	flex: 0 0 auto;
	min-width: 0;
	border-right: 1px solid #fff;
	box-sizing: border-box;
}

.visit-segment:last-child {
	border-right: 0;
}

.site-visits {
	white-space: nowrap;
	font-size: 0.7rem;
	font-weight: 600;
	color: #212529;
	font-variant-numeric: tabular-nums;
	text-align: right;
}

.note {
	margin: 8px 0 0;
	font-size: 0.66rem;
	line-height: 1.35;
	color: #6c757d;
}
</style>
